import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Partners = () => {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        website: '',
        order: 0,
        is_active: true,
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await axios.get('https://pitac.gov.pk/api/admin/partners', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setPartners(response.data);
        } catch (error) {
            console.error('Error fetching partners:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setServerError(null);
        
        const formDataToSend = new FormData();
        
        // Append all form data
        formDataToSend.append('name', formData.name);
        formDataToSend.append('website', formData.website || '');
        formDataToSend.append('order', parseInt(formData.order) || 0);
        formDataToSend.append('is_active', formData.is_active ? 1 : 0);
        
        if (logoFile) {
            formDataToSend.append('logo', logoFile);
        }

        try {
            const token = localStorage.getItem('admin_token');
            
            if (editing) {
                // ✅ FIX: Use POST with _method=PUT for FormData
                formDataToSend.append('_method', 'PUT');
                const response = await axios.post(
                    `https://pitac.gov.pk/api/admin/partners/${editing.id}`,
                    formDataToSend,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                        }
                    }
                );
                console.log('Update success:', response.data);
            } else {
                // For create - use POST
                const response = await axios.post(
                    'https://pitac.gov.pk/api/admin/partners',
                    formDataToSend,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                        }
                    }
                );
                console.log('Create success:', response.data);
            }
            
            fetchPartners();
            resetForm();
        } catch (error) {
            console.error('=== FULL ERROR DETAILS ===');
            console.error('Error object:', error);
            
            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);
                
                if (error.response.status === 422) {
                    const validationErrors = error.response.data.errors || {};
                    setErrors(validationErrors);
                    
                    const errorMessages = Object.values(validationErrors).flat();
                    if (errorMessages.length > 0) {
                        setServerError('Validation Error: ' + errorMessages.join(', '));
                    } else {
                        setServerError('Please check your input and try again.');
                    }
                } else if (error.response.status === 500) {
                    let errorMessage = 'Internal server error occurred';
                    if (error.response.data && typeof error.response.data === 'object') {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        }
                        if (error.response.data.error) {
                            errorMessage = error.response.data.error;
                        }
                    }
                    setServerError(errorMessage);
                }
            } else if (error.request) {
                setServerError('No response from server. Please check if the server is running.');
            } else {
                setServerError(error.message);
            }
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this partner?')) {
            try {
                const token = localStorage.getItem('admin_token');
                await axios.delete(`https://pitac.gov.pk/api/admin/partners/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                fetchPartners();
            } catch (error) {
                console.error('Error deleting partner:', error);
            }
        }
    };

    const handleEdit = (partner) => {
        console.log('Editing partner:', partner);
        setEditing(partner);
        setFormData({
            name: partner.name || '',
            website: partner.website || '',
            order: partner.order || 0,
            is_active: partner.is_active === 1 || partner.is_active === true,
        });
        setLogoPreview(partner.logo_path || null);
        setShowModal(true);
        setErrors({});
        setServerError(null);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            website: '',
            order: 0,
            is_active: true,
        });
        setLogoFile(null);
        setLogoPreview(null);
        setEditing(null);
        setShowModal(false);
        setErrors({});
        setServerError(null);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    if (loading) return <div className="admin-loading">Loading partners...</div>;

    return (
        <div>
            <div className="admin-header">
                <h2>Partners Management</h2>
                <button className="admin-add-btn" onClick={() => setShowModal(true)}>
                    + Add New Partner
                </button>
            </div>

            {partners.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7a8f' }}>
                    <p>No partners found. Click "Add New Partner" to create one.</p>
                </div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Logo</th>
                                <th>Name</th>
                                <th>Website</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {partners.map((partner) => (
                                <tr key={partner.id}>
                                    <td>
                                        {partner.logo_path ? (
                                            <img 
                                                src={`https://pitac.gov.pk${partner.logo_path}`} 
                                                alt={partner.name}
                                                className="admin-image-preview-thumb"
                                                style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>No logo</span>
                                        )}
                                    </td>
                                    <td><strong>{partner.name}</strong></td>
                                    <td>{partner.website || '-'}</td>
                                    <td>
                                        <span className={`admin-status-badge ${partner.is_active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                                            {partner.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="admin-actions">
                                            <button className="admin-edit-btn" onClick={() => handleEdit(partner)}>Edit</button>
                                            <button className="admin-delete-btn" onClick={() => handleDelete(partner.id)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="admin-modal-overlay" onClick={() => resetForm()}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>{editing ? 'Edit Partner' : 'Add New Partner'}</h2>
                            <button className="admin-modal-close" onClick={resetForm}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {serverError && (
                                <div className="admin-server-error" style={{ 
                                    backgroundColor: '#f8d7da', 
                                    color: '#721c24', 
                                    padding: '0.75rem', 
                                    borderRadius: '0.375rem',
                                    marginBottom: '1rem',
                                    border: '1px solid #f5c6cb',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    <strong>Error:</strong> {serverError}
                                </div>
                            )}
                            <div className="admin-form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    required
                                />
                                {errors.name && <div className="admin-error" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                    {errors.name[0]}
                                </div>}
                            </div>
                            <div className="admin-form-group">
                                <label>Website</label>
                                <input
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                                    placeholder="https://example.com"
                                />
                                {errors.website && <div className="admin-error" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                    {errors.website[0]}
                                </div>}
                            </div>
                            <div className="admin-form-group">
                                <label>Order</label>
                                <input
                                    type="number"
                                    value={formData.order}
                                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                                />
                                {errors.order && <div className="admin-error" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                    {errors.order[0]}
                                </div>}
                            </div>
                            <div className="admin-form-group">
                                <label>Logo</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                />
                                {errors.logo && <div className="admin-error" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                    {errors.logo[0]}
                                </div>}
                                {logoPreview && (
                                    <div style={{ marginTop: '10px' }}>
                                        <img 
                                            src={logoPreview} 
                                            alt="Preview" 
                                            className="admin-image-preview"
                                            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="admin-form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>
                            <button type="submit" className="admin-submit-btn">
                                {editing ? 'Update Partner' : 'Create Partner'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Partners;